export type SchedulePeriod = 'AM' | 'PM'

const DEFAULT_SCHEDULE_TIME = { hour: '08', minute: '00', period: 'AM' as SchedulePeriod }
const TIME_PART_FORMATTER_CACHE = new Map<string, Intl.DateTimeFormat>()

function toHour24(hour: string, period: SchedulePeriod) {
  let hour24 = Number(hour)
  if (period === 'PM' && hour24 < 12) hour24 += 12
  if (period === 'AM' && hour24 === 12) hour24 = 0
  return hour24
}

function getTimeZoneFormatter(timeZone: string) {
  const key = timeZone || 'UTC'
  const cached = TIME_PART_FORMATTER_CACHE.get(key)
  if (cached) return cached
  const formatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: key,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  })
  TIME_PART_FORMATTER_CACHE.set(key, formatter)
  return formatter
}

function getTimeZoneParts(date: Date, timeZone: string) {
  const parts = getTimeZoneFormatter(timeZone).formatToParts(date)
  const byType = Object.fromEntries(parts.map((part) => [part.type, part.value]))
  return {
    year: Number(byType['year']),
    month: Number(byType['month']),
    day: Number(byType['day']),
    hour: Number(byType['hour']),
    minute: Number(byType['minute']),
  }
}

function getTimeZoneOffsetMinutes(date: Date, timeZone: string) {
  const parts = getTimeZoneParts(date, timeZone)
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute)
  return (asUtc - date.getTime()) / 60000
}

function zonedLocalToUtcDate(year: number, month: number, day: number, hour: number, minute: number, timeZone: string) {
  const guess = new Date(Date.UTC(year, month, day, hour, minute))
  const firstOffset = getTimeZoneOffsetMinutes(guess, timeZone)
  const firstUtc = new Date(guess.getTime() - firstOffset * 60000)
  const secondOffset = getTimeZoneOffsetMinutes(firstUtc, timeZone)
  return new Date(guess.getTime() - secondOffset * 60000)
}

export function getSelectedUtcTime(hour: string, minute: string, period: SchedulePeriod) {
  const hour24 = toHour24(hour, period)

  const now = new Date()
  const localDate = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hour24, Number(minute))
  const utcHour = String(localDate.getUTCHours()).padStart(2, '0')
  const utcMinute = String(localDate.getUTCMinutes()).padStart(2, '0')
  return `${utcHour}:${utcMinute}`
}

export function getSelectedUtcTimeForTimeZone(hour: string, minute: string, period: SchedulePeriod, timeZone: string) {
  if (!timeZone) return getSelectedUtcTime(hour, minute, period)

  const hour24 = toHour24(hour, period)
  const now = new Date()
  const zoneToday = getTimeZoneParts(now, timeZone)
  const utcDate = zonedLocalToUtcDate(zoneToday.year, zoneToday.month - 1, zoneToday.day, hour24, Number(minute), timeZone)
  const utcHour = String(utcDate.getUTCHours()).padStart(2, '0')
  const utcMinute = String(utcDate.getUTCMinutes()).padStart(2, '0')
  return `${utcHour}:${utcMinute}`
}

export function revertUtcTime(utcTime?: string): { hour: string; minute: string; period: SchedulePeriod } {
  if (!utcTime) return DEFAULT_SCHEDULE_TIME

  const [hour, minute] = utcTime.split(':').map((value) => Number(value))
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return DEFAULT_SCHEDULE_TIME
  }

  const now = new Date()
  const utcDate = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), hour, minute))
  const localHour24 = utcDate.getHours()
  const localMinute = String(utcDate.getMinutes()).padStart(2, '0')
  const period: SchedulePeriod = localHour24 >= 12 ? 'PM' : 'AM'
  const localHour12 = localHour24 % 12 || 12

  return {
    hour: String(localHour12).padStart(2, '0'),
    minute: localMinute,
    period,
  }
}

export function revertTimeString(time?: string): { hour: string; minute: string; period: SchedulePeriod } {
  if (!time) return DEFAULT_SCHEDULE_TIME

  const [hourValue, minuteValue] = time.split(':').map((value) => Number(value))
  const hour = hourValue ?? Number.NaN
  const minute = minuteValue ?? Number.NaN
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) {
    return DEFAULT_SCHEDULE_TIME
  }

  const period: SchedulePeriod = hour >= 12 ? 'PM' : 'AM'
  const hour12 = hour % 12 || 12
  return {
    hour: String(hour12).padStart(2, '0'),
    minute: String(minute).padStart(2, '0'),
    period,
  }
}

export function formatUtcTimeToLocalDisplay(utcTime?: string) {
  if (!utcTime) return '-'

  const [hourStr, minuteStr] = utcTime.split(':')
  const utcHour = Number(hourStr)
  const utcMinute = Number(minuteStr)
  if (!Number.isFinite(utcHour) || !Number.isFinite(utcMinute)) return utcTime

  const now = new Date()
  const utcDate = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), utcHour, utcMinute))
  const localHour24 = utcDate.getHours()
  const minute = String(utcDate.getMinutes()).padStart(2, '0')
  const period: SchedulePeriod = localHour24 >= 12 ? 'PM' : 'AM'
  const hour12 = localHour24 % 12 || 12
  return `${String(hour12).padStart(2, '0')}:${minute} ${period}`
}
